import { SafeReportResponse } from "../Conversion";
import { formatAbbreviatedMoney, formatExactMoneyDollars } from "../Money";
import * as client from "../../../client";

export const sortedLayersFromReport = (
  specialtyPropertyInfo: SafeReportResponse
) => {
  const layers = specialtyPropertyInfo.report_json.layer_info;

  return layers.sort((a, b) => {
    // 1. Primary (starting_point=0) before excess
    const aIsPrimary = a.starting_point === 0 ? 0 : 1;
    const bIsPrimary = b.starting_point === 0 ? 0 : 1;
    if (aIsPrimary !== bIsPrimary) return aIsPrimary - bIsPrimary;

    // 2. By starting point ascending
    if (a.starting_point !== b.starting_point)
      return a.starting_point - b.starting_point;

    // 3. By size (range) ascending
    return a.range - b.range;
  });
};

export const readableLayer = (layer: client.Layer) => {
  const { starting_point, range, participation } = layer;
  const isPartial =
    !!participation && participation > 0 && participation < range;
  const base =
    starting_point === 0
      ? `Primary ${formatAbbreviatedMoney(range)}`
      : `${formatAbbreviatedMoney(range)} xs ${formatAbbreviatedMoney(
          starting_point
        )}`;
  return isPartial
    ? `${formatAbbreviatedMoney(participation)} p/o ${base}`
    : base;
};

export const readableLimitOfLiabilityFromLayer = (layer: client.Layer) => {
  const { starting_point, range, participation } = layer;

  const effectiveParticipation = participation ?? range;
  const hasExcess = starting_point > 0;
  const isPartial = effectiveParticipation < range;

  let result = "";

  if (isPartial && hasExcess) {
    // e.g. $10M p/o $25M x/s $25M per occurrence
    result = `${formatExactMoneyDollars(effectiveParticipation)} p/o ${formatExactMoneyDollars(range)} x/s ${formatExactMoneyDollars(starting_point)}`;
  } else if (isPartial) {
    // e.g. $5M part of $10M per occurrence
    result = `${formatExactMoneyDollars(effectiveParticipation)} part of ${formatExactMoneyDollars(range)}`;
  } else if (hasExcess) {
    // e.g. $5M x/s $5M per occurrence
    result = `${formatExactMoneyDollars(range)} x/s ${formatExactMoneyDollars(starting_point)}`;
  } else {
    // e.g. $10M per occurrence
    result = formatExactMoneyDollars(range);
  }

  return `${result} per occurrence`;
};

export function hasOverlappingLayers(objects: client.Layer[]): boolean {
  const overlappingPairs: [client.Layer, client.Layer][] = [];

  // Compare each object with every other object
  for (let i = 0; i < objects.length; i++) {
    for (let j = i + 1; j < objects.length; j++) {
      const obj1 = objects[i];
      const obj2 = objects[j];

      // Calculate the end points for each range
      const obj1End = obj1.starting_point + obj1.range;
      const obj2End = obj2.starting_point + obj2.range;

      // Check for overlap
      // Two ranges overlap if the start of one range is less than the end of the other range
      // AND the end of the first range is greater than the start of the other range
      if (obj1.starting_point < obj2End && obj1End > obj2.starting_point) {
        overlappingPairs.push([obj1, obj2]);
      }
    }
  }

  return overlappingPairs.length > 0;
}
